/**
 * AcceptInvite — V2 Premium Experience (V2.0 Phase 5, Cluster A).
 *
 * Route: `/accept-invite/:token`. Public team-invitation acceptance page.
 * Validates the token, shows invite metadata, then creates the account.
 * On success the backend sets a session cookie and we redirect to `/admin`.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AuthShell from "./AuthShell";
import { V2Input, V2Button, V2Alert } from "@/components/v2/FormPrimitives";

const ROLE_LABELS = {
  admin: "Admin",
  manager: "Manager",
  support: "Support",
  dispute_specialist: "Dispute specialist",
  funding_specialist: "Funding specialist",
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [phase, setPhase] = useState("loading");
  const [invite, setInvite] = useState(null);
  const [reason, setReason] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get(
          `/auth/accept-invite/${encodeURIComponent(token)}`,
          { validateStatus: (s) => (s >= 200 && s < 300) || s === 404 || s === 410 },
        );
        if (cancelled) return;
        if (!data?.valid) {
          setReason(data?.reason || "Invitation could not be loaded.");
          setPhase("invalid");
        } else {
          setInvite(data);
          setPhase("form");
        }
      } catch (e) {
        if (cancelled) return;
        setReason(formatApiError(e));
        setPhase("invalid");
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setPhase("submitting");
    try {
      await api.post("/auth/accept-invite", {
        token,
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      await refresh();
      setPhase("done");
      toast.success("Welcome to the team!");
      setTimeout(() => navigate("/admin", { replace: true }), 600);
    } catch (e) {
      setError(formatApiError(e));
      setPhase("form");
    }
  }

  const titleMap = {
    loading: "Verifying invitation…",
    invalid: "This invitation can't be used.",
    form: "Join Informed OS.",
    submitting: "Join Informed OS.",
    done: "You're in!",
  };

  return (
    <AuthShell testId="accept-invite-page" title={titleMap[phase] || "Accept invitation"}>
      {phase === "loading" && (
        <div
          data-testid="accept-invite-loading"
          className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8 text-center text-v2-body-sm text-v2-textMuted"
        >
          Verifying invitation…
        </div>
      )}

      {phase === "invalid" && (
        <div
          data-testid="accept-invite-invalid"
          className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8"
        >
          <p className="text-v2-body-md text-v2-textMuted mb-6">{reason}</p>
          <Link
            to="/login"
            data-testid="accept-invite-login-link"
            className="inline-flex items-center gap-2 rounded-v2-md border border-v2-border px-4 py-2 text-v2-body-sm text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
          >
            Go to sign-in
          </Link>
        </div>
      )}

      {(phase === "form" || phase === "submitting") && invite && (
        <div className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8">
          <div className="text-v2-caption uppercase tracking-[0.16em] text-v2-accent mb-2">
            You've been invited
          </div>
          <p
            data-testid="accept-invite-heading"
            className="text-v2-body-md text-v2-textMuted mb-6 leading-relaxed"
          >
            <strong className="text-v2-text">{invite.invited_by_email}</strong> invited{" "}
            <strong className="text-v2-text" data-testid="accept-invite-email">{invite.email}</strong>{" "}
            to join the operations team as a{" "}
            <strong className="text-v2-accent" data-testid="accept-invite-role">
              {ROLE_LABELS[invite.team_role] || invite.team_role}
            </strong>
            .
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <V2Input
                label="First name"
                testId="accept-invite-first-name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
              <V2Input
                label="Last name"
                testId="accept-invite-last-name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
            <V2Input
              label="Create password"
              testId="accept-invite-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <V2Input
              label="Confirm password"
              testId="accept-invite-confirm-password"
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
            {error && (
              <V2Alert tone="danger" testId="accept-invite-error">{error}</V2Alert>
            )}
            <V2Button
              type="submit"
              disabled={phase === "submitting"}
              data-testid="accept-invite-submit"
              fullWidth
              loading={phase === "submitting"}
            >
              {phase === "submitting" && <Loader2 className="w-4 h-4 animate-spin" />}
              {phase === "submitting" ? "Creating account…" : "Accept invitation & sign in"}
            </V2Button>
            <p className="text-v2-body-sm text-v2-textSubtle leading-relaxed pt-1">
              By accepting, you agree to act under the operations team policy. Your role
              permissions can be changed by the workspace owner at any time.
            </p>
          </form>
        </div>
      )}

      {phase === "done" && (
        <div
          data-testid="accept-invite-done"
          className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8 text-center"
        >
          <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-v2-success" />
          <p className="text-v2-body-md text-v2-textMuted">Redirecting to the admin console…</p>
        </div>
      )}
    </AuthShell>
  );
}
